import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _delay from 'lodash/delay';
import _find from 'lodash/find';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mInstallation from 'models/installation';
// Custom
import {
  getInstallationsList, deleteInstallation, getCompanies, refreshList,
} from 'actions';
import { fullListShape } from 'constants/PropTypesShapes';
import { dateGenerator, dateFormatter } from 'utils/DateUtils';
import { addPath, navigateToShow } from 'installations/navigation';
import { AddTextButton } from 'components/buttons/TextButtons';
import MainListTemplate from 'components/templates/MainListTemplate';
import withPermissions from 'hocs/WithPermissions';
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withLoading from 'hocs/WithLoading';
import withTitle from 'hocs/WithTitle';
import InstallationFilters from 'installations/assets/main/InstallationFilters';
import InstallationRow from 'installations/assets/main/InstallationRow';
import InstallationRowDetails from 'installations/assets/main/InstallationRowDetails';

class InstallationsList extends React.Component {
  constructor(props) {
    super(props);

    this.handleDeleteInstallation = this.handleDeleteInstallation.bind(this);
    this.handleRowDbleClick = this.handleRowDbleClick.bind(this);
    this.handleAsyncOperationsPeriodChange = this.handleAsyncOperationsPeriodChange.bind(this);
    this.doGetRowComp = this.doGetRowComp.bind(this);
    this.doGetRowCompDetails = this.doGetRowCompDetails.bind(this);

    this.state = {
      asyncOperationsPeriod: mInstallation.asyncOperationsPeriods[0],
    };
  }

  componentDidMount() {
    const { hasPermission, handleGetCompanies } = this.props;

    if (hasPermission({ needed: permissions.pAdmin })) {
      handleGetCompanies();
    }
  }

  componentDidUpdate(prevProps) {
    const { deleted, handleRefreshList } = this.props;
    // Operations catching
    if (!prevProps.deleted && deleted) {
      handleRefreshList();
    }
  }

  // Handlers
  handleDeleteInstallation(installation) {
    const { handleDisplayConfirm, handleDeleteInstallation } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.deleteInstallation', values: { installationName: installation.name } },
      () => handleDeleteInstallation(installation.id),
    );
  }

  handleRowDbleClick(installation) {
    const { handleStartLoading } = this.props;
    if (installation) {
      // Display loader and display installation
      handleStartLoading();
      _delay(() => navigateToShow(installation.id), 500);
    }
  }

  handleAsyncOperationsPeriodChange(value) {
    const { handleRefreshList } = this.props;
    this.setState({
      asyncOperationsPeriod: _find(mInstallation.asyncOperationsPeriods, ['value', value]),
    }, handleRefreshList);
  }

  // Private
  doGetRowComp(props) {
    const { asyncOperationsPeriod } = this.state;
    return (
      <InstallationRow
        {...props}
        asyncOperationsErrorsDays={asyncOperationsPeriod.days}
        onDelete={this.handleDeleteInstallation}
      />
    );
  }

  doGetRowCompDetails(props) {
    return (
      <InstallationRowDetails {...props} />
    );
  }

  render() {
    const { installations, hasPermission, handleGetInstallationsList } = this.props;
    const { asyncOperationsPeriod } = this.state;

    // Permissions
    const { pInstallation, pAdmin } = permissions;

    // Extra params
    const extraParams = [];
    if (hasPermission({ permissions: { needed: pAdmin } })) {
      // Build date from which async operations metrics are loaded
      extraParams.push({
        key: 'async_operation_rates_since',
        value: dateFormatter.toServerFormat(
          dateGenerator.dateAdder(
            dateGenerator.dateSubtractor(
              dateGenerator.now(), asyncOperationsPeriod.subtractor,
            ),
            { minutes: 1 }, // Add 1 minute to avoid reaching server minimum accepted date
          ),
        ),
      });
    }

    return (
      <MainListTemplate
        title={<LocalizedMessage id="title.installationsList" />}
        icon={mInstallation.icon}
        displayRefresh
        actionButtons={[
          <OnlyWith
            key="add"
            permissions={{
              needed: pInstallation.write,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <AddTextButton primary path={addPath()} />
          </OnlyWith>,
        ]}
        name={mInstallation.name}
        listFeatures={mInstallation.listFeatures({ useAdminSort: hasPermission({ needed: pAdmin }) })}
        withActions
        nbActions={2}
        withDetails={hasPermission({ needed: pAdmin })}
        elementsData={{
          loading: installations.loading,
          elements: installations.elements,
          handleGetList: (p, s, f) => handleGetInstallationsList(p, s, f, extraParams),
        }}
        onRowDbleClick={this.handleRowDbleClick}
        filtersComp={(
          <InstallationFilters
            asyncOperationsPeriodValue={asyncOperationsPeriod.value}
            onAsyncOperationsPeriodChange={this.handleAsyncOperationsPeriodChange}
          />
        )}
        rowComp={this.doGetRowComp}
        rowCompDetails={this.doGetRowCompDetails}
      />
    );
  }
}

InstallationsList.propTypes = {
  installations: fullListShape.isRequired,
  deleted: PropTypes.bool.isRequired,
  handleRefreshList: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleGetInstallationsList: PropTypes.func.isRequired,
  handleDeleteInstallation: PropTypes.func.isRequired,
  handleGetCompanies: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  installations: state.installation.list,
  deleted: state.installation.delete.deleted,
});

const mapDispatchToProps = (dispatch) => ({
  handleRefreshList: () => {
    dispatch(refreshList());
  },
  handleGetInstallationsList: (pagination, sort, filters, extraParams) => {
    dispatch(getInstallationsList(pagination, sort, filters, extraParams));
  },
  handleDeleteInstallation: (id) => {
    dispatch(deleteInstallation(id));
  },
  handleGetCompanies: () => {
    dispatch(getCompanies());
  },
});

module.exports = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withPermissions,
  withConfirmDialog,
  withLoading,
  withTitle('pageTitle.installations'),
)(InstallationsList);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/routes/list/components/InstallationsList.js